import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRefresh, faSearch, faPrint, faDownload } from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { useTranslation } from 'react-i18next';

const PurchaseDaybookActions = ({
    searchTerm,
    onSearchChange,
    onRefresh,
    onPrint,
    printLoading,
    onExport,
    exportLoading,
    fromDate,
    toDate,
    onFromDateChange,
    onToDateChange,
    loading,
    canExport = false,
    selectedCount = 0,
    onBulkDelete,
}) => {
    const { t } = useTranslation();

    return (
        <div className="p-6 bg-white rounded-2xl shadow-lg mb-4">
            <div className="flex flex-wrap items-center gap-3 justify-between">
                {/* Search + Date Range grouped together */}
                <div className="flex items-center gap-3 flex-1 w-full min-w-0 sm:min-w-[220px] max-w-3xl">
                    <div className="relative flex-1">
                        <FontAwesomeIcon icon={faSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder={t('purchaseDaybook.actions.searchPlaceholder')}
                            value={searchTerm}
                            onChange={(e) => onSearchChange?.(e.target.value)}
                            disabled={loading}
                            className="w-full pl-12 pr-4 py-3 border-2 rounded-xl focus:ring-2 transition-all duration-200"
                            style={{
                                backgroundColor: UI_COLORS.FORMS.INPUT.background,
                                borderColor: UI_COLORS.FORMS.INPUT.border,
                                color: UI_COLORS.FORMS.INPUT.text,
                                '--tw-ring-color': colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.3),
                            }}
                        />
                    </div>

                    <div className="flex-shrink-0 flex items-center gap-2">
                        <input
                            type="date"
                            value={fromDate || ''}
                            onChange={(e) => onFromDateChange?.(e.target.value)}
                            disabled={loading}
                            className="px-3 py-2 rounded-xl border-2"
                            style={{
                                backgroundColor: UI_COLORS.FORMS.INPUT.background,
                                borderColor: UI_COLORS.FORMS.INPUT.border,
                                color: UI_COLORS.FORMS.INPUT.text,
                            }}
                            aria-label={t('purchaseDaybook.actions.fromPlaceholder')}
                        />
                        <input
                            type="date"
                            value={toDate || ''}
                            onChange={(e) => onToDateChange?.(e.target.value)}
                            disabled={loading}
                            className="px-3 py-2 rounded-xl border-2"
                            style={{
                                backgroundColor: UI_COLORS.FORMS.INPUT.background,
                                borderColor: UI_COLORS.FORMS.INPUT.border,
                                color: UI_COLORS.FORMS.INPUT.text,
                            }}
                            aria-label={t('purchaseDaybook.actions.toPlaceholder')}
                        />
                    </div>
                </div>

                {/* Actions (right aligned) */}
                <div className="flex flex-wrap items-center gap-3 justify-end flex-shrink-0 w-full sm:w-auto">
                    {selectedCount > 0 && (
                        <button
                            onClick={onBulkDelete}
                            disabled={loading}
                            className="px-4 py-3 rounded-xl font-semibold transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                            style={{
                                backgroundColor: UI_COLORS.STATUS.ERROR,
                                color: '#fff',
                                border: `2px solid ${UI_COLORS.STATUS.ERROR}`,
                            }}
                        >
                            <span className="flex items-center gap-2">
                                <span className="flex items-center justify-center w-6 h-6 bg-white/20 rounded-full text-xs">
                                    {selectedCount}
                                </span>
                                <span className="hidden sm:inline">Bulk Delete</span>
                                <span className="sm:hidden">Delete</span>
                            </span>
                        </button>
                    )}

                    {canExport && (
                        <button
                            onClick={onExport}
                            disabled={loading || exportLoading}
                            className="px-4 py-3 rounded-xl font-semibold transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                            style={{
                                backgroundColor: UI_COLORS.STATUS.SUCCESS,
                                color: '#fff',
                                border: `2px solid ${UI_COLORS.STATUS.SUCCESS}`,
                            }}
                        >
                            <span className="flex items-center gap-2">
                                <FontAwesomeIcon icon={faDownload} className={exportLoading ? 'animate-bounce' : ''} />
                                <span className="hidden sm:inline">{exportLoading ? 'Exporting...' : 'Export'}</span>
                            </span>
                        </button>
                    )}

                    <button
                        onClick={onRefresh}
                        disabled={loading}
                        className="px-4 py-3 rounded-xl font-semibold transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{
                            backgroundColor: 'transparent',
                            color: UI_COLORS.TEXT.PRIMARY,
                            border: `2px solid ${UI_COLORS.BORDERS.MEDIUM}`,
                        }}
                    >
                        <span className="flex items-center gap-2">
                            <FontAwesomeIcon icon={faRefresh} className={loading ? 'animate-spin' : ''} />
                            <span className="hidden sm:inline">{t('common.buttons.refresh')}</span>
                        </span>
                    </button>

                    {onPrint && (
                        <button
                            onClick={onPrint}
                            disabled={loading || printLoading}
                            className="px-4 py-3 rounded-xl font-semibold transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                            style={{
                                backgroundColor: UI_COLORS.BRAND.PRIMARY,
                                color: '#fff',
                                border: `2px solid ${UI_COLORS.BRAND.PRIMARY}`,
                            }}
                        >
                            <span className="flex items-center gap-2">
                                <FontAwesomeIcon icon={faPrint} className={printLoading ? 'animate-spin' : ''} />
                                <span className="hidden sm:inline">{printLoading ? t('common.actions.loading') : t('common.actions.print')}</span>
                            </span>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PurchaseDaybookActions;
